import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchIncidentsByRole } from "../store/slices/incidentSlice";
import IncidentForm from "./IncidentForm";
import { getIncidentStatusLog, getRoleBasedIncidents } from "../services/api";

const QUICK_FILTERS = {
  all: {
    label: "All Incidents",
    status: "all",
    params: { dashboardFilter: "all" },
  },
  open: {
    label: "Open Incidents",
    status: "open",
    params: { dashboardFilter: "open" },
  },
  closed: {
    label: "Closed Incidents",
    status: "closed",
    params: { dashboardFilter: "closed" },
  },
  recent: {
    label: "Recent 7 Days",
    status: "all",
    params: { dashboardFilter: "recent" },
  },
  unread: {
    label: "Unread Incidents",
    status: "all",
    params: { dashboardFilter: "unread" },
  },
};

const Dashboard = () => {

  const dispatch = useDispatch();
  const { incidents = [], totalCount = 0 } =
  useSelector((state) => state.incidents) || {};
  const user = useSelector((state) => state.auth.user);

  const role = user?.role;

  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [statusIncident, setStatusIncident] = useState(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: 'date',
    direction: 'desc',
  });

  const [selectedFilter, setSelectedFilter] = useState("unread");
  const [filterRefreshId, setFilterRefreshId] = useState(0);
  const currentFilter = QUICK_FILTERS[selectedFilter] || QUICK_FILTERS.open;
  const [dashboardStats, setDashboardStats] = useState({
    all: 0,
    open: 0,
    closed: 0,
    recent: 0,
    unread: 0,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const loadDashboardStats = useCallback(async () => {
    const [allRes, openRes, closedRes, recentRes, unreadRes] = await Promise.all([
      getRoleBasedIncidents(1, 1, QUICK_FILTERS.all.status, QUICK_FILTERS.all.params),
      getRoleBasedIncidents(1, 1, QUICK_FILTERS.open.status, QUICK_FILTERS.open.params),
      getRoleBasedIncidents(1, 1, QUICK_FILTERS.closed.status, QUICK_FILTERS.closed.params),
      getRoleBasedIncidents(1, 1, QUICK_FILTERS.recent.status, QUICK_FILTERS.recent.params),
      getRoleBasedIncidents(1, 1, QUICK_FILTERS.unread.status, QUICK_FILTERS.unread.params),
    ]);

    setDashboardStats({
      all: allRes.data?.totalCount || 0,
      open: openRes.data?.totalCount || 0,
      closed: closedRes.data?.totalCount || 0,
      recent: recentRes.data?.totalCount || 0,
      unread: unreadRes.data?.totalCount || 0,
    });
  }, []);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    dispatch(fetchIncidentsByRole({
      page,
      limit,
      status: currentFilter.status,
      extraParams: currentFilter.params,
    }));
  }, [dispatch, page, limit, selectedFilter, filterRefreshId, currentFilter.status, currentFilter.params]);

  useEffect(() => {
    loadDashboardStats().catch(() => {
      setDashboardStats({ all: 0, open: 0, closed: 0, recent: 0, unread: 0 });
    });
  }, [loadDashboardStats]);

  /* ================= STATUS COLORS ================= */
  const statusColors = {
    'OVR Submitted': 'warning',
    'Sent for User Comment': 'info',
    'User Comment Received': 'primary',
    'Investigation Completed': 'info',
    'Staff Comment Received': 'primary',
    'Sent for HOD Review': 'primary',
    'HOD Comment Submitted': 'secondary',
    'Final Review': 'dark',
    Closed: 'success'
  };

  /* ================= OPEN MODAL ================= */
  const openIncident = (incident) => {
    setSelectedIncident(incident);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedIncident(null);
    setShowModal(false);
    refreshDashboard();
  };

  const refreshDashboard = () => {
    setFilterRefreshId((current) => current + 1);
    loadDashboardStats().catch(() => {});
  };

  const totalIncidents = dashboardStats.open + dashboardStats.closed;
  const canViewUserNames = ['Admin', 'Quality Manager', 'Quality Staff'].includes(role);
  const openPercent = totalIncidents ? Math.round((dashboardStats.open / totalIncidents) * 100) : 0;
  const closedPercent = totalIncidents ? 100 - openPercent : 0;
  const statusSummary = incidents.reduce((summary, incident) => {
    const status = incident.currentStatus || "Unknown";
    return {
      ...summary,
      [status]: (summary[status] || 0) + 1,
    };
  }, {});
  const maxStatusCount = Math.max(1, ...Object.values(statusSummary));
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const sortValueGetters = {
    refNo: (incident) => incident.refNo || '',
    date: (incident) => incident.date ? new Date(incident.date).getTime() : 0,
    createdBy: (incident) => incident.createdBy?.username || '',
    priority: (incident) => incident.immediateActions?.priority || '',
    status: (incident) => incident.currentStatus || '',
  };
  const sortedIncidents = [...incidents].sort((a, b) => {
    const getValue = sortValueGetters[sortConfig.key] || sortValueGetters.date;
    const aValue = getValue(a);
    const bValue = getValue(b);
    const direction = sortConfig.direction === 'asc' ? 1 : -1;

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return (aValue - bValue) * direction;
    }

    return String(aValue).localeCompare(String(bValue)) * direction;
  });

  const requestSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const applyQuickFilter = (filterKey) => {
    setPage(1);
    setSelectedFilter(filterKey);
    setFilterRefreshId((current) => current + 1);
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up" aria-hidden="true"></i>;
    return sortConfig.direction === 'asc'
      ? <i className="bi bi-sort-up" aria-hidden="true"></i>
      : <i className="bi bi-sort-down" aria-hidden="true"></i>;
  };

  const getPageNumbers = () => {
    const pages = [];
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    const end = Math.min(totalPages, start + 4);

    for (let current = start; current <= end; current += 1) {
      pages.push(current);
    }

    return pages;
  };

  const openStatusHistory = async (incident) => {
    const { data } = await getIncidentStatusLog(incident._id);
    setStatusIncident(incident);
    setStatusHistory(data || []);
  };

  const closeStatusHistory = () => {
    setStatusIncident(null);
    setStatusHistory([]);
  };

  const getLogUserName = (log) => {
    if (!canViewUserNames && log?.status === "OVR Submitted") return "****";
    if (log?.createdByName) return log.createdByName;
    const createdBy = log?.createdBy;
    if (!createdBy) return "System";
    if (typeof createdBy === "string") return "System";
    return createdBy.username || createdBy.name || createdBy.fullName || createdBy.email || "System";
  };

  const formatLogDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const formatLogTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  return (
    <div className="dashboard-page">

      <div className="metric-grid">
        <button
          type="button"
          className={`metric-card metric-card-all ${selectedFilter === "all" ? "active" : ""}`}
          onClick={() => applyQuickFilter("all")}
        >
          <div className="metric-icon all"><i className="bi bi-grid-3x3-gap" aria-hidden="true"></i></div>
          <div>
            <span>All Incidents</span>
            <strong>{dashboardStats.all}</strong>
          </div>
        </button>
        <button
          type="button"
          className={`metric-card metric-card-open ${selectedFilter === "open" ? "active" : ""}`}
          onClick={() => applyQuickFilter("open")}
        >
          <div className="metric-icon open"><i className="bi bi-folder2-open" aria-hidden="true"></i></div>
          <div>
            <span>Open Incidents</span>
            <strong>{dashboardStats.open}</strong>
          </div>
        </button>
        <button
          type="button"
          className={`metric-card metric-card-closed ${selectedFilter === "closed" ? "active" : ""}`}
          onClick={() => applyQuickFilter("closed")}
        >
          <div className="metric-icon closed"><i className="bi bi-check2-circle" aria-hidden="true"></i></div>
          <div>
            <span>Closed Incidents</span>
            <strong>{dashboardStats.closed}</strong>
          </div>
        </button>
        <button
          type="button"
          className={`metric-card metric-card-recent ${selectedFilter === "recent" ? "active" : ""}`}
          onClick={() => applyQuickFilter("recent")}
        >
          <div className="metric-icon recent"><i className="bi bi-clock-history" aria-hidden="true"></i></div>
          <div>
            <span>Recent 7 Days</span>
            <strong>{dashboardStats.recent}</strong>
          </div>
        </button>
        <button
          type="button"
          className={`metric-card metric-card-unread ${selectedFilter === "unread" ? "active" : ""}`}
          onClick={() => applyQuickFilter("unread")}
        >
          <div className="metric-icon unread"><i className="bi bi-envelope-exclamation" aria-hidden="true"></i></div>
          <div>
            <span>Unread Incidents</span>
            <strong>{dashboardStats.unread}</strong>
          </div>
        </button>
      </div>

      <div className="dashboard-insights">
        <div className="insight-panel">
          <div className="panel-title">Open vs Closed</div>
          <div className="donut-wrap">
            <div
              className="status-donut"
              style={{
                background: `conic-gradient(#2563eb 0 ${openPercent}%, #16a34a ${openPercent}% 100%)`
              }}
            >
              <div>
                <strong>{totalIncidents}</strong>
                <span>Total</span>
              </div>
            </div>
            <div className="donut-legend">
              <span><i className="legend-dot open"></i>Open {openPercent}%</span>
              <span><i className="legend-dot closed"></i>Closed {closedPercent}%</span>
            </div>
          </div>
        </div>

        <div className="insight-panel">
          <div className="panel-title">Current Page Status</div>
          <div className="status-bars">
            {Object.entries(statusSummary).length ? (
              Object.entries(statusSummary).map(([status, count]) => (
                <div className="status-bar-row" key={status}>
                  <div className="status-bar-label">
                    <span>{status}</span>
                    <strong>{count}</strong>
                  </div>
                  <div className="status-bar-track">
                    <div style={{ width: `${Math.max(8, (count / maxStatusCount) * 100)}%` }}></div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-muted small">No incidents to summarize.</div>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-list-panel">
        <div className="dashboard-list-header">
          <div className="modern-tabs" role="tablist" aria-label="Incident status filter">
            <button
              type="button"
              className={selectedFilter === 'unread' ? 'active' : ''}
              onClick={() => applyQuickFilter("unread")}
            >
              Unread
              <span>{dashboardStats.unread}</span>
            </button>

            <button
              type="button"
              className={selectedFilter === 'open' ? 'active' : ''}
              onClick={() => applyQuickFilter("open")}
            >
              Open
              <span>{dashboardStats.open}</span>
            </button>

            <button
              type="button"
              className={selectedFilter === 'closed' ? 'active' : ''}
              onClick={() => applyQuickFilter("closed")}
            >
              Closed
              <span>{dashboardStats.closed}</span>
            </button>

            <button
              type="button"
              className={selectedFilter === 'all' ? 'active' : ''}
              onClick={() => applyQuickFilter("all")}
            >
              All
              <span>{dashboardStats.all}</span>
            </button>
          </div>
          <div className="dashboard-table-tools">
            <label>
              Rows
              <select
                value={limit}
                onChange={(event) => {
                  setPage(1);
                  setLimit(Number(event.target.value));
                }}
              >
                {[10, 25, 50].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="grid-icon-btn"
              onClick={refreshDashboard}
              title="Refresh incidents"
              aria-label="Refresh incidents"
            >
              <i className="bi bi-arrow-repeat" aria-hidden="true"></i>
            </button>
            <div className="list-count">{currentFilter.label}: {totalCount} records</div>
          </div>
        </div>

      {/* ================= TABLE ================= */}
      <div className="modern-table-wrap">
      <table className="table modern-dashboard-table">
        <thead>
          <tr>
            <th>
              <button type="button" className="sort-header-btn" onClick={() => requestSort('refNo')}>
                RefNo {renderSortIcon('refNo')}
              </button>
            </th>
            <th className="indicator-col" aria-label="Unread"></th>
            <th>
              <button type="button" className="sort-header-btn" onClick={() => requestSort('date')}>
                Date {renderSortIcon('date')}
              </button>
            </th>
            <th>
              <button type="button" className="sort-header-btn" onClick={() => requestSort('createdBy')}>
                Created By {renderSortIcon('createdBy')}
              </button>
            </th>
            <th>
              <button type="button" className="sort-header-btn" onClick={() => requestSort('priority')}>
                Priority {renderSortIcon('priority')}
              </button>
            </th>
            <th>Assigned</th> {/* ✅ NEW */}
            <th>
              <button type="button" className="sort-header-btn" onClick={() => requestSort('status')}>
                Status {renderSortIcon('status')}
              </button>
            </th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {sortedIncidents?.length > 0 ? (
            sortedIncidents.map((incident) => (

              <tr key={incident._id} className={incident.isUnread ? "incident-row-unread" : ""}>

                {/* REF */}
                <td>{incident.refNo}</td>
                <td className="indicator-col">
                  {incident.isUnread && (
                    <span
                      className="unread-indicator"
                      title="Unread incident"
                      aria-label="Unread incident"
                    ></span>
                  )}
                </td>

                {/* DATE */}
                <td>
                  {incident.date
                    ? new Date(incident.date).toLocaleDateString()
                    : ''}
                </td>

                {/* CREATED BY (ROLE BASED MASKING) */}
                <td>
                  {(role === 'Admin' ||
                    role === 'Quality Manager' ||
                    role === 'Quality Staff')
                    ? incident.createdBy?.username
                    : '****'}
                </td>

                {/* PRIORITY */}
                <td>
                  {incident.immediateActions?.priority || ''}
                </td>

                {/* ASSIGNED */}
                <td>
                  {incident.isAssignedToMe && (
                    <span className="badge bg-info">Assigned</span>
                  )}
                </td>

                {/* STATUS */}
                <td>
                  <button
                    type="button"
                    className={`status-pill bg-${statusColors[incident.currentStatus] || 'secondary'}`}
                    onClick={() => openStatusHistory(incident)}
                    title="View status history"
                  >
                    {incident.currentStatus}
                  </button>
                </td>

                {/* ACTION */}
                <td>
                  <button
                    className="btn btn-sm btn-primary modern-view-btn"
                    onClick={() => openIncident(incident)}
                  >
                    View
                  </button>
                </td>

              </tr>

            ))
          ) : (
            <tr>
              <td colSpan="8" className="text-center">
                No incidents found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

      {/* ================= PAGINATION ================= */}
      <div className="dashboard-pagination">

        <div className="pagination-summary">
          Showing {totalCount === 0 ? 0 : ((page - 1) * limit) + 1}
          -{Math.min(page * limit, totalCount)} of {totalCount}
        </div>

        <div className="modern-pagination">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage(1)}
            title="First page"
          >
            <i className="bi bi-chevron-double-left" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            title="Previous page"
          >
            <i className="bi bi-chevron-left" aria-hidden="true"></i>
          </button>

          {getPageNumbers().map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              className={pageNumber === page ? 'active' : ''}
              onClick={() => setPage(pageNumber)}
            >
              {pageNumber}
            </button>
          ))}

          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
            title="Next page"
          >
            <i className="bi bi-chevron-right" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage(totalPages)}
            title="Last page"
          >
            <i className="bi bi-chevron-double-right" aria-hidden="true"></i>
          </button>
        </div>

      </div>
      </div>

      {/* ================= MODAL ================= */}
      {showModal && (
        <div className="modal d-block incident-modal" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-fullscreen">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">Incident Details</h5>
                <button className="btn-close" onClick={closeModal}></button>
              </div>

              <div className="modal-body overflow-auto">
                <IncidentForm
                  incident={selectedIncident}
                  closeModal={closeModal}
                  refreshDashboardData={refreshDashboard}
                />
              </div>

            </div>
          </div>
        </div>
      )}

      {statusIncident && (
        <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)", zIndex: 2050 }}>
          <div className="modal-dialog modal-dialog-centered status-history-dialog">
            <div className="modal-content status-history-content">
              <div className="modal-header">
                <h5 className="modal-title">Status History - {statusIncident.refNo}</h5>
                <button className="btn-close" onClick={closeStatusHistory}></button>
              </div>
              <div className="modal-body status-history-body">
                {statusHistory.length ? (
                  <div className="status-timeline">
                    {statusHistory.map((log, index) => (
                      <div key={log._id} className="status-timeline-item">
                        <div className="status-timeline-marker">
                          <span>{statusHistory.length - index}</span>
                        </div>
                        <div className="status-timeline-content">
                          <div className="status-timeline-status">{log.status}</div>
                          <div className="status-timeline-meta">
                            <span>
                              <i className="bi bi-person" aria-hidden="true"></i>
                              {getLogUserName(log)}
                            </span>
                            <span>
                              <i className="bi bi-calendar-event" aria-hidden="true"></i>
                              {formatLogDate(log.createdDate)}
                            </span>
                            <span>
                              <i className="bi bi-clock" aria-hidden="true"></i>
                              {formatLogTime(log.createdDate)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted">No status history found.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
